/**
 * A3 — Bandeja «Sin asignar»: lo que ha entrado por cualquier canal y todavía no cuelga de
 * ningún cliente. Solo ves lo tuyo (tus correos y lo que registraste): un correo personal
 * que no es de un cliente no se publica al resto del bufete.
 */
import { useCallback, useEffect, useState } from 'react'
import { Ban, Loader2, RotateCcw, Search, UserPlus, UserCheck, X } from 'lucide-react'
import { api } from '../../lib/api'
import ClientFormModal from '../clients/ClientFormModal'
import AssignCommunicationModal from './AssignCommunicationModal'
import { CHANNEL_ICON, DirectionBadge, inputCls } from '../communications/common'
import { useAuth } from '../../contexts/AuthContext'
import {
  CommunicationChannelLabel,
  type CreateClientInput,
  type UnassignedItem,
  type UnassignedListResponse,
} from '@toga/shared'

const PAGE_SIZE = 25

interface Props {
  /** Avisa del nº de pendientes (insignia de la pestaña). */
  onCount: (pending: number) => void
}

/** «Ana Ruiz López» → datos para un cliente nuevo; si no hay nombre, se usa el email. */
function prefillFor(item: UnassignedItem): Partial<CreateClientInput> {
  const parts = (item.counterpartName ?? '').trim().split(/\s+/).filter(Boolean)
  return {
    type: 'PERSONA',
    firstName: parts[0] ?? '',
    lastName1: parts.slice(1).join(' '),
    email: item.counterpartEmail ?? '',
    phone: item.counterpartPhone ?? '',
  }
}

export default function UnassignedPanel({ onCount }: Props) {
  const auth = useAuth()
  const canWrite = auth.status === 'authenticated' && auth.user.role !== 'LECTURA'
  const [status, setStatus] = useState<'SIN_ASIGNAR' | 'DESCARTADA'>('SIN_ASIGNAR')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<UnassignedListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const [assigning, setAssigning] = useState<{
    item: UnassignedItem
    client?: { id: string; displayName: string }
  } | null>(null)
  const [creating, setCreating] = useState<UnassignedItem | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])
  useEffect(() => setPage(1), [status, debouncedQ])

  const load = useCallback(async () => {
    setError(null)
    try {
      const p = new URLSearchParams({ status, page: String(page), pageSize: String(PAGE_SIZE) })
      if (debouncedQ) p.set('q', debouncedQ)
      const r = await api.get<UnassignedListResponse>(`/communications/unassigned?${p}`)
      setData(r)
      onCount(r.pendingCount)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar')
    }
  }, [status, page, debouncedQ, onCount])

  useEffect(() => {
    void load()
  }, [load, tick])

  const reload = () => setTick((t) => t + 1)

  const act = async (item: UnassignedItem, fn: () => Promise<unknown>, msg?: string) => {
    setBusyId(item.id)
    setError(null)
    try {
      await fn()
      if (msg) setNotice(msg)
      reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo completar la acción')
    } finally {
      setBusyId(null)
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1

  return (
    <div className="surface overflow-hidden">
      <div className="px-5 py-3.5 border-b border-base space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1.5">
            {(
              [
                ['SIN_ASIGNAR', 'Pendientes', data?.pendingCount],
                ['DESCARTADA', 'Descartadas', data?.discardedCount],
              ] as const
            ).map(([value, label, n]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatus(value)}
                className={[
                  'px-3 py-1.5 rounded-lg text-xs border',
                  status === value
                    ? 'border-toga-500/50 bg-toga-500/10 t-primary'
                    : 'border-base t-secondary hover:bg-hover',
                ].join(' ')}
              >
                {label} {n != null && <span className="t-faint">{n}</span>}
              </button>
            ))}
          </div>
          <div className="relative min-w-[220px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 t-faint" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar…"
              className={`${inputCls} pl-8`}
            />
          </div>
        </div>
        <p className="t-faint text-xs">
          Solo tú ves esta bandeja. Asigna cada comunicación a su cliente, o descártala si no es de
          ninguno.
        </p>
      </div>

      {notice && (
        <div className="mx-5 mt-3 text-xs rounded-lg px-3 py-2 border text-emerald-300 bg-emerald-500/10 border-emerald-500/30 flex items-center justify-between">
          {notice}
          <button type="button" onClick={() => setNotice(null)} aria-label="Cerrar">
            <X size={12} />
          </button>
        </div>
      )}
      {error && (
        <div className="mx-5 mt-3 text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}
      {!data && !error && (
        <div className="px-5 py-8 t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      )}
      {data && data.items.length === 0 && (
        <div className="px-5 py-12 text-center t-muted text-sm">
          {status === 'SIN_ASIGNAR'
            ? 'No tienes nada pendiente de asignar.'
            : 'No hay comunicaciones descartadas.'}
        </div>
      )}

      {data && data.items.length > 0 && (
        <ul>
          {data.items.map((item) => (
            <li key={item.id} className="px-5 py-3.5 border-b border-base/50 last:border-b-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="t-faint">{CHANNEL_ICON[item.channel]}</span>
                <span className="t-primary text-sm font-medium truncate max-w-[60ch]">
                  {item.subject || CommunicationChannelLabel[item.channel]}
                </span>
                <DirectionBadge direction={item.direction} />
                <span className="ml-auto t-faint text-xs whitespace-nowrap">
                  {new Date(item.occurredAt).toLocaleString('es-ES', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
              </div>
              <div className="t-faint text-xs mt-0.5">
                {[item.counterpartName, item.counterpartEmail, item.counterpartPhone]
                  .filter(Boolean)
                  .join(' · ') || 'Sin contacto indicado'}
              </div>
              {item.summary && (
                <div className="t-muted text-xs mt-1 whitespace-pre-line line-clamp-2">
                  {item.summary}
                </div>
              )}

              {item.proposals.length > 0 && status === 'SIN_ASIGNAR' && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  <span className="t-faint text-xs">¿Será de…?</span>
                  {item.proposals.map((p) => (
                    <button
                      key={p.clientId}
                      type="button"
                      disabled={!canWrite}
                      onClick={() =>
                        setAssigning({
                          item,
                          client: { id: p.clientId, displayName: p.displayName },
                        })
                      }
                      title={p.label}
                      className={[
                        'text-xs px-2 py-0.5 rounded-full border',
                        p.confidence === 'HIGH'
                          ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10'
                          : p.confidence === 'MEDIUM'
                            ? 'border-sky-500/40 text-sky-300 bg-sky-500/10'
                            : 'border-base t-secondary',
                      ].join(' ')}
                    >
                      {p.displayName}
                      <span className="t-faint ml-1">· {p.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {canWrite && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {status === 'SIN_ASIGNAR' ? (
                    <>
                      <ActionBtn
                        onClick={() => setAssigning({ item })}
                        icon={<UserCheck size={12} />}
                      >
                        Asignar
                      </ActionBtn>
                      <ActionBtn onClick={() => setCreating(item)} icon={<UserPlus size={12} />}>
                        Crear cliente
                      </ActionBtn>
                      <ActionBtn
                        disabled={busyId === item.id}
                        onClick={() =>
                          void act(item, () => api.post(`/communications/${item.id}/discard`))
                        }
                        icon={<Ban size={12} />}
                      >
                        Descartar
                      </ActionBtn>
                      {item.source === 'EMAIL' && item.counterpartEmail && (
                        <ActionBtn
                          disabled={busyId === item.id}
                          onClick={() => {
                            if (
                              !confirm(`¿Ignorar siempre los correos de ${item.counterpartEmail}?`)
                            )
                              return
                            void act(item, async () => {
                              const r = await api.post<{ discarded: number }>(
                                '/communications/ignore-sender',
                                { email: item.counterpartEmail }
                              )
                              setNotice(
                                `Remitente ignorado: ${r.discarded} correo(s) descartado(s). Los próximos entrarán ya descartados.`
                              )
                            })
                          }}
                          icon={<Ban size={12} />}
                        >
                          Ignorar remitente
                        </ActionBtn>
                      )}
                    </>
                  ) : (
                    <ActionBtn
                      disabled={busyId === item.id}
                      onClick={() =>
                        void act(item, () => api.post(`/communications/${item.id}/restore`))
                      }
                      icon={<RotateCcw size={12} />}
                    >
                      Devolver a pendientes
                    </ActionBtn>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {data && data.total > data.pageSize && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-base text-xs t-muted">
          <span>
            Página {data.page} de {totalPages} · {data.total}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-2.5 py-1 rounded-lg border border-base t-secondary hover:bg-hover disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-2.5 py-1 rounded-lg border border-base t-secondary hover:bg-hover disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {assigning && (
        <AssignCommunicationModal
          item={assigning.item}
          initialClient={assigning.client}
          onClose={() => setAssigning(null)}
          onDone={(ruleCreated) => {
            setAssigning(null)
            setNotice(
              ruleCreated
                ? 'Asignada. Desde ahora los correos de ese remitente se archivarán solos.'
                : 'Asignada.'
            )
            reload()
          }}
        />
      )}
      {creating && (
        <ClientFormModal
          initial={null}
          prefill={prefillFor(creating)}
          onClose={() => setCreating(null)}
          onSaved={(id) => {
            const item = creating
            setCreating(null)
            if (!id) {
              reload()
              return
            }
            // El nombre definitivo es el que quedó en la ficha (el usuario pudo cambiarlo).
            api
              .get<{ client: { displayName: string } }>(`/clients/${id}`)
              .then((r) =>
                setAssigning({ item, client: { id, displayName: r.client.displayName } })
              )
              .catch(() => setAssigning({ item, client: { id, displayName: 'Cliente nuevo' } }))
          }}
        />
      )}
    </div>
  )
}

function ActionBtn({
  children,
  icon,
  onClick,
  disabled,
}: {
  children: string
  icon: React.ReactNode
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-2.5 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-200 inline-flex items-center gap-1.5 disabled:opacity-50"
    >
      {icon}
      {children}
    </button>
  )
}
